<template>
  <div class="api-keys">
    <TableCard
      ref="datatable"
      v-model:sort="sorting"
      v-model:page="page"
      v-model:page-size="pageSize"
      class="api__keys ui-m-xxl-t"
      :loading="loading"
      :columns="columns"
      :sort-by-name="sortByName"
      :sort-dir-name="sortDirName"
      :rows="rows"
      :actions="actions"
      :count="$store.state.profile.totalRows"
      :formaters="formaters"
      :show-total="false"
      extend-height
      is-static
      dont-fill-empty
      i18n-path="reports"
      @fetch-data="fetchData"
    >
      <template v-slot:header>
        <span class="api-keys__header ui-g-md">
          <span class="api-keys__header-text">{{ $t("profile.apiKeys.title") }}</span>
          <Tooltip
            class="api-keys__header-tooltip"
            :title="$t('profile.apiKeys.tip')"
            placement="bottom-right"
          >
            <i class="fas fa-question-circle pl-6" />
          </Tooltip>
        </span>
      </template>

      <template v-slot:actionsPrepend>
        <div class="ui-g-md ui-mobile-12">
          <Tooltip
            class="tooltip-btn-disabled"
            :title="$t('tooltip_limits.apiKeys')"
            placement="bottom-left"
            :disabled="!createDisabled"
          >
            <a
              class="btn btn-primary btn-sm ui-mobile-12"
              :class="{
                disabled: btnDisable
              }"
              :disabled="createDisabled || undefined"
              @click="createAccessKey"
            >+ {{ $t("profile.apiKeys.createButton") }}</a>
          </Tooltip>
        </div>
      </template>
    </TableCard>

    <CreateAccessKey ref="createAccessKey" />
  </div>
</template>

<script>
import moment from 'moment';
import Formaters from '@/views/Dashboard/CustomReports/formaters';
import TableCard from '@/components/Cards/TableCard.vue';
import UrlMixin from '@/components/Query/url-mixin';
import UrlMixinHooks from '@/components/Query/url-mixin-hooks';
import DimensionsMixin from '@/components/Report/Components/DimensionsMixin';
import InjectPlugins from '@/components/Report/Components/inject-plugins';
import { pageSizeMixin, resolvePageSize } from '@/views/Dashboard/Reports/page_size';
import HelpersMixin from '@/components/Helpers/helpers-mixin';
import tableFlowMixinGenerator from '@/views/Dashboard/Reports/tableFlowMixin';
import Tooltip from '@/components/Tooltip.vue';
import UiTableAction from '@/components/ui/UiTableAction.vue';
import UiTablePopoverAction from '@/components/ui/UiTablePopoverAction.vue';
import CreateAccessKey from './CreateAccessKey.vue';

const sizes = {
  1080: 10,
  1440: 20,
};
const viewName = '/api-keys';
const pageLimitProp = 'pageSize';
const pageSize = resolvePageSize(viewName, {
  _default: 10,
  sizes,
});

const defaultParams = {
  page: 1,
  pageSize,
  sort_column: 'created_at',
  sort_dir: 'desc',
};

const fetch = (ctx, params) => {
  if (ctx.$_.isEmpty(params)) {
    params = {
      ...defaultParams,
    };
  }

  const apikey = () => ctx.$store.dispatch('getApiKeys', {
    offset: ctx.pageSize * (ctx.page - 1) || 0,
    ...params,
    limit: ctx.pageSize || pageSize,
    sort_column: 'created_at',
    sort_dir: params.sort_dir || 'desc',
  });

  const count = () => ctx.$store.dispatch('getApiKeysCount');

  if (ctx.lastQuery) {
    ctx.lastQuery = { ...params };
  }

  return Promise.all([apikey(), count()]);
};

const tableFlowMixin = tableFlowMixinGenerator({
  defaultParams: {
    ...defaultParams,
  },
  params: Object.keys(defaultParams),
  fetch,
  windowRefreshHook: true,
  fetchOnMounted: true,
  sortByName: 'sort_column',
  sortDirName: 'sort_dir',
});

export default {
  fetch,
  components: {
    CreateAccessKey,
    TableCard,
    Tooltip,
  },
  mixins: [
    UrlMixin,
    UrlMixinHooks,
    DimensionsMixin,
    InjectPlugins,
    pageSizeMixin,
    HelpersMixin,
    tableFlowMixin,
  ],
  props: ['btnDisable', 'fetchedData', 'passedParams'],
  data() {
    return {
      viewName,
      pageLimitProp,
      isLoading: false,
      messageDelete: this.$t('topNavbar.wantDelete'),
      columns: [{
        name: 'key',
        filterable: false,
      }, {
        name: 'status',
        filterable: false,
        align: 'center',
      }, {
        name: 'created_at',
        filterable: false,
        sortable: true,
        align: 'center',
        format: 'date-time',
      }, {
        name: 'last_active',
        filterable: false,
        align: 'center',
      }],
      lastQuery: {},
      loading: false,
      formaters: Formaters,
      isTableReady: false,
    };
  },
  computed: {
    rows() {
      return this.$store.state.profile.apiKeys.map((e) => ({
        key: e.key,
        status: this.$t(`postbacks.card.${e.status}`),
        created_at: e.created_at,
        last_active: e.last_active ? moment(e.last_active).format('DD MMM YYYY HH:mm') : this.$t('profile.apiKeys.table.never'),
        data: {
          id: e.id,
        },
      }));
    },
    totalRows() {
      return this.$store.state.profile.totalRows;
    },
    createDisabled() {
      const { max_api_keys } = this.$store.state.settings.limitations;
      return max_api_keys !== null && this.$store.state.profile.totalRows >= max_api_keys;
    },
    actions() {
      const operations = [
        {
          name: 'activate-deactivate',
          componentAlt: {
            component: UiTableAction,
            props: (row) => {
              const isActive = row.status === this.$t('postbacks.card.active');
              const params = isActive
                ? {
                  tooltipContent: this.$t('profile.apiKeys.table.deactivate'),
                  iconClass:  'fas fa-stop-circle',
                  colorPreset: 'red',
                }
                : {
                  tooltipContent: this.$t('profile.apiKeys.table.activate'),
                  iconClass:  'fas fa-play-circle',
                  colorPreset: 'green',
                };

              return {
                ...params,
                onClick: () => {
                  if (isActive) {
                    this.deactivateKey(row);
                  } else {
                    this.activateKey(row);
                  }
                },
              };
            },
          },
        },
        {
          name: 'delete',
          componentAlt: {
            component: UiTablePopoverAction,
            props: (row) => ({
              tooltipContent: this.$t('promoCodes.promocodes.delete'),
              iconClass: 'far fa-trash-alt',
              colorPreset: 'red',
              width: 250,
              message: `${this.$t('topNavbar.wantDelete')}?`,
              buttons: [{
                text: this.$t('mediaBuying.channels.cancel'),
              }, {
                text: this.$t('promoCodes.promocodes.delete'),
                type: 'danger',
                click: () => this.deleteKey(row),
              }],
            }),
          },
        },
      ];

      return {
        width: 100,
        name: () => this.$t('profile.apiKeys.table.actions'),
        operations: operations,
      };
    },
  },
  created() {
    this.actions.operations.map((e) => {
      if (e.component) {
        e.component = e.component.bind(this);
      }
    });
    this.setFromQuery(this.passedParams);
  },
  methods: {
    createAccessKey() {
      if (!this.createDisabled) {
        this.$confirm.open('password', {
          title:  this.$t('profile.apiKeys.passwordRequired'),
          callback: (pwd, modal) => {
            modal.isLoading = true;
            this.$store
              .dispatch('createApiKey', { password: pwd })
              .then((data) => {
                modal.close();
                this.$store.dispatch('getApiKeysLazy', { ...this.offsetParams, ...this.sort });
                this.$refs.createAccessKey.open(data);
              })
              .finally(() => modal.isLoading = false);
          },
        });
      }
    },
    deactivateKey(row) {
      if (this.isLoading) return;
      this.isLoading = true;
      this.$store
        .dispatch('deactivateApiKey', { id: row.data.id, ...this.offsetParams, ...this.sort })
        .then(() => {
          this.$alert.success(this.$t('profile.apiKeys.apiKeyDeactivated'));
        })
        .finally(() => this.isLoading = false);
    },
    activateKey(row) {
      if (this.isLoading) return;
      this.isLoading = true;
      this.$store
        .dispatch('activateApiKey', { id: row.data.id, ...this.offsetParams, ...this.sort })
        .then(() => {
          this.$alert.success(this.$t('profile.apiKeys.apiKeyActivated'));
        })
        .catch(() => {
          this.$alert.error(this.$t('profile.apiKeys.unexpectedError'));
        })
        .finally(() => this.isLoading = false);
    },
    deleteKey(row) {
      if (this.isLoading) return;
      this.isLoading = true;
      this.$store
        .dispatch('deleteApiKey', row.data.id)
        .then(() => {
          this.$store.dispatch('getApiKeysLazy', { ...this.offsetParams, ...this.sort });
          this.$store.dispatch('getApiKeysCountLazy');
          this.$alert.success(this.$t('profile.apiKeys.apiKeyDeleted'));
        })
        .catch(() => {
          this.$alert.error(this.$t('profile.apiKeys.unexpectedError'));
        })
        .finally(() => this.isLoading = false);
    },
  },
};
</script>

<style lang="scss">
// дубликатов нет
@import 'src/assets/theme/default/profile/apikeys';

.api-keys {
  &__header {
    align-items: center;
    .ui-tooltip {
      width: unset;
    }
  }

  &__header-text {
    font-size: 16px;
    font-weight: 600;
  }

  &__header-tooltip {
    color: var(--active-color);
    font-size: 14px;
    align-self: center;
  }
}
</style>
